import * as React from 'react'
import * as PropTypes from 'prop-types'
import { createComponent } from '@cloudflare/style-container'

export type TFlexPassthroughProps = { direction?: 'row' | 'column' }
export const FlexPassthroughRule = ({ direction = 'row' }: TFlexPassthroughProps) => ({
  display: 'flex',
  flexDirection: direction,
  flex: 1,
})
export const FlexPassthrough = createComponent(FlexPassthroughRule)

export type TLightTabsActive = { active: string }
export type TLightTabsProps = TLightTabsActive & {
  borderColor?: string
  onChange: (id: string) => void
  children: React.ReactNode[]
}

export type TLightTabsState = {
  borderWidth: number
  borderX: number
  borderY: number
}

export type TLightTabContext = TLightTabsActive & {
  requestChange: (id: string) => void
}

export class LightTabs extends React.Component<TLightTabsProps, TLightTabsState> {
  public static childContextTypes = {
    active: PropTypes.string,
    requestChange: PropTypes.func,
  }

  public static defaultProps: Partial<TLightTabsProps> = {
    borderColor: '#1a4599',
  }

  public innerWrapperEl: HTMLDivElement

  constructor(props: TLightTabsProps) {
    super(props)

    this.state = {
      borderWidth: 0,
      borderX: 0,
      borderY: 0,
    }

    this.onInnerWrapperRefAvailable = this.onInnerWrapperRefAvailable.bind(this)
    this.onTabRequestChange = this.onTabRequestChange.bind(this)
  }

  getChildContext(): TLightTabContext {
    return {
      active: this.props.active,
      requestChange: this.onTabRequestChange,
    }
  }

  shouldComponentUpdate(nextProps: TLightTabsProps, nextState: TLightTabsState) {
    return (
      nextProps.children !== this.props.children ||
      nextProps.active !== this.props.active ||
      nextState.borderX !== this.state.borderX ||
      nextState.borderY !== this.state.borderY ||
      nextState.borderWidth !== this.state.borderWidth
    )
  }

  componentDidUpdate() {
    this.renderBorder()
  }

  componentDidMount() {
    this.renderBorder()

    // EW-286 Fix tab border with DIRTY HACK
    // TODO: Don't do this
    setTimeout(() => {
      this.renderBorder()
    }, 1000)
  }

  renderBorder() {
    if (!this.innerWrapperEl) return

    const el = this.innerWrapperEl.querySelector(
      `[data-tab-id="${this.props.active}"]`,
    ) as HTMLDivElement

    if (!el) return

    this.setState({
      borderWidth: el.offsetWidth,
      borderX: el.offsetLeft,
    })
  }

  render() {
    const { borderWidth, borderX, borderY } = this.state

    return (
      <TabsWrapper>
        <div ref={this.onInnerWrapperRefAvailable}>{this.props.children}</div>
        <TabBorderTransformer x={borderX} y={borderY} width={borderWidth}>
          <TabBorder color={this.props.borderColor} />
        </TabBorderTransformer>
      </TabsWrapper>
    )
  }

  onInnerWrapperRefAvailable(el: HTMLDivElement) {
    this.innerWrapperEl = el
    this.renderBorder()
  }

  onTabRequestChange(id: string) {
    this.props.onChange(id)
  }
}

export type TLightTabProps = {
  id: string
  disabled?: boolean
  href?: string
}

export class LightTab extends React.Component<TLightTabProps> {
  public static contextTypes = LightTabs.childContextTypes

  context: TLightTabContext

  constructor(props: TLightTabProps) {
    super(props)
    this.onClick = this.onClick.bind(this)
  }

  render() {
    return (
      <Tab
        data-tab-id={this.props.id}
        disabled={this.props.disabled}
        active={this.context.active === this.props.id}
        href={this.props.href}
        onClick={this.onClick}
      >
        {this.props.children}
      </Tab>
    )
  }

  onClick(e: MouseEvent) {
    if (this.props.disabled) return

    e.preventDefault()
    this.context.requestChange(this.props.id)
  }
}

export class LightTabsBody extends React.Component<TLightTabsActive> {
  public static childContextTypes = {
    active: LightTabs.childContextTypes.active,
  }

  getChildContext(): TLightTabsActive {
    return { active: this.props.active }
  }

  render() {
    return <FlexPassthrough direction="column">{this.props.children}</FlexPassthrough>
  }
}

export type TLightTabBodyProps = {
  shouldUnmountOnChange?: boolean
} & TLightTabProps
export class LightTabBody extends React.Component<TLightTabBodyProps> {
  public static contextTypes = LightTabsBody.childContextTypes
  public static defaultProps: Partial<TLightTabBodyProps> = {
    shouldUnmountOnChange: true,
  }

  context: TLightTabContext

  render() {
    if (this.props.shouldUnmountOnChange) {
      return (
        this.context.active === this.props.id && (
          <FlexPassthrough direction="column">{this.props.children}</FlexPassthrough>
        )
      )
    }

    return (
      <LightTabBodyWrapper active={this.context.active === this.props.id}>
        <FlexPassthrough direction="column">{this.props.children}</FlexPassthrough>
      </LightTabBodyWrapper>
    )
  }
}

const LightTabBodyWrapper = createComponent(
  ({ active = false }: { active: boolean }) => ({
    display: active ? 'flex' : 'none',
    flexDirection: 'column',
    flex: 1,
  }),
)

const Tab = createComponent(
  ({
    active = false,
    disabled = false,
  }: {
    active: boolean
    disabled: boolean
    href?: string
    onClick: (e: MouseEvent) => void
  }) => ({
    position: 'relative',
    display: 'inline-block',
    padding: '0.74rem 1rem',
    fontSize: '0.9rem',
    color: active ? 'inherit' : '#7d7d7d',
    cursor: 'pointer',
    ':hover': {
      color: disabled ? '#7d7d7d' : 'inherit',
      cursor: disabled ? 'not-allowed' : 'pointer',
    },
  }),
  'a',
  ['data-tab-id', 'href', 'onClick'],
)

export const TabsWrapper = createComponent(() => ({
  position: 'relative',
}))

export const TabsInnerWrapper = createComponent(
  (props: { ref: (el: HTMLDivElement) => void }) => ({}),
  'div',
  ['ref'],
)

export const TabBorder = createComponent(
  ({ size = 3, color = 'blue' }: { size?: number; color?: string }) => ({
    height: `${size}px`,
    borderRadius: `${size}px`,
    background: color,
  }),
)

export const TabBorderTransformer = ({
  x,
  y,
  width,
  children,
}: {
  x: number
  y: number
  width: number
  children: any
}) => {
  return (
    <span
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        transition: 'all 0.17s ease-out',
        transform: `translate(${x}px, ${y}px)`,
        width: `${width}px`,
      }}
    >
      {children}
    </span>
  )
}



// WEBPACK FOOTER //
// ./node_modules/@cloudflare/light-tabs/src/LightTabs.tsx